import type {
  AuditLogEntry,
  ChannelPermissionOverride,
  Emoji,
  Guild,
  GuildBan,
  GuildDetail,
  GuildMemberRow,
  GuildRole,
  Invite,
  MuteLevel,
} from '../../types/index.js';
import type { RequestFn } from '../http.js';

/** Guild management: guilds, members/moderation, roles, channel overrides,
 *  custom emoji and invites. */
export function makeGuildsApi(request: RequestFn) {
  return {
    listGuilds: () => request<{ guilds: Guild[] }>('/api/guilds'),
    getGuild: (id: string) => request<GuildDetail>(`/api/guilds/${id}`),
    /** One roster page after `after` (a user id from `membersNextCursor`). */
    listGuildMembers: (id: string, after: string, limit?: number) =>
      request<{ members: GuildMemberRow[]; nextCursor: string | null }>(
        `/api/guilds/${id}/members?after=${encodeURIComponent(after)}${limit ? `&limit=${limit}` : ''}`,
      ),
    createGuild: (input: { name: string; iconUrl?: string }) =>
      request<Guild>('/api/guilds', { method: 'POST', body: JSON.stringify(input) }),
    updateGuild: (id: string, input: { name?: string; iconUrl?: string | null }) =>
      request<Guild>(`/api/guilds/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    deleteGuild: (id: string) => request<void>(`/api/guilds/${id}`, { method: 'DELETE' }),
    // Undo a soft-delete within the grace window (owner only).
    restoreGuild: (id: string) =>
      request<void>(`/api/guilds/${id}/restore`, { method: 'POST' }),
    // Soft-deleted guilds I own that are still restorable (within the grace window).
    listDeletedGuilds: () =>
      request<{
        guilds: Array<{ id: string; name: string; iconUrl: string | null; deletedAt: string }>;
      }>(`/api/me/deleted-guilds`),
    leaveGuild: (id: string) => request<void>(`/api/guilds/${id}/leave`, { method: 'POST' }),
    /** Set this guild's notification level (server-wide mute). `mutedUntil` is
     *  an ISO string for a timed mute, or null/omitted for a permanent one. */
    setGuildMute: (guildId: string, level: MuteLevel, mutedUntil?: string | null) =>
      request<{ guildId: string; level: MuteLevel; mutedUntil: string | null }>(
        `/api/guilds/${guildId}/mute`,
        { method: 'PUT', body: JSON.stringify({ level, mutedUntil: mutedUntil ?? null }) },
      ),
    /** Clear this guild's mute (back to the default ALL — server deletes the row). */
    clearGuildMute: (guildId: string) =>
      request<{ guildId: string; level: 'ALL'; mutedUntil: null }>(
        `/api/guilds/${guildId}/mute`,
        { method: 'DELETE' },
      ),
    kickMember: (guildId: string, userId: string) =>
      request<void>(`/api/guilds/${guildId}/members/${userId}`, { method: 'DELETE' }),
    listBans: (guildId: string) => request<{ bans: GuildBan[] }>(`/api/guilds/${guildId}/bans`),
    banMember: (
      guildId: string,
      userId: string,
      input: { reason?: string | null; expiresAt?: string | null } = {},
    ) =>
      request<GuildBan>(`/api/guilds/${guildId}/bans/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    unbanMember: (guildId: string, userId: string) =>
      request<void>(`/api/guilds/${guildId}/bans/${userId}`, { method: 'DELETE' }),
    timeoutMember: (
      guildId: string,
      userId: string,
      input: { timeoutUntil: string | null; reason?: string | null },
    ) =>
      request<GuildMemberRow>(`/api/guilds/${guildId}/members/${userId}/timeout`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    listAuditLogs: (guildId: string) =>
      request<{ logs: AuditLogEntry[] }>(`/api/guilds/${guildId}/audit-logs`),
    updateGuildMember: (
      guildId: string,
      userId: string,
      input: { roleIds: string[]; nickname?: string | null },
    ) =>
      request<GuildMemberRow>(`/api/guilds/${guildId}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    createRole: (
      guildId: string,
      input: { name: string; color?: number; permissions?: string; position?: number },
    ) =>
      request<GuildRole>(`/api/guilds/${guildId}/roles`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateRole: (
      guildId: string,
      roleId: string,
      input: { name?: string; color?: number; permissions?: string; position?: number },
    ) =>
      request<GuildRole>(`/api/guilds/${guildId}/roles/${roleId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    deleteRole: (guildId: string, roleId: string) =>
      request<void>(`/api/guilds/${guildId}/roles/${roleId}`, { method: 'DELETE' }),
    reorderRoles: (guildId: string, orders: { id: string; position: number }[]) =>
      request<void>(`/api/guilds/${guildId}/roles/reorder`, {
        method: 'POST',
        body: JSON.stringify({ orders }),
      }),
    upsertChannelOverride: (
      guildId: string,
      channelId: string,
      input: { roleId: string; allow: string; deny: string },
    ) =>
      request<ChannelPermissionOverride>(
        `/api/guilds/${guildId}/channels/${channelId}/overrides`,
        {
          method: 'PUT',
          body: JSON.stringify(input),
        },
      ),
    deleteChannelOverride: (guildId: string, channelId: string, roleId: string) =>
      request<void>(`/api/guilds/${guildId}/channels/${channelId}/overrides/${roleId}`, {
        method: 'DELETE',
      }),

    // ---------- custom server emoji ----------
    listEmojis: (guildId: string) =>
      request<{ emojis: Emoji[] }>(`/api/guilds/${guildId}/emojis`),
    createEmoji: (guildId: string, body: { name: string; imageUrl: string }) =>
      request<Emoji>(`/api/guilds/${guildId}/emojis`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    deleteEmoji: (guildId: string, emojiId: string) =>
      request<void>(`/api/guilds/${guildId}/emojis/${emojiId}`, {
        method: 'DELETE',
      }),

    /** Persist the user's custom server-rail order. `order` is the full list of
     *  guild ids in the desired display sequence. */
    reorderGuilds: (order: string[]) =>
      request<{ ok: boolean }>('/api/me/guilds/reorder', {
        method: 'POST',
        body: JSON.stringify({ order }),
      }),

    // ---------- invites ----------
    listInvites: (guildId: string) =>
      request<{ invites: Invite[] }>(`/api/guilds/${guildId}/invites`),
    createInvite: (
      guildId: string,
      input: { channelId?: string; expiresInS?: number; maxUses?: number } = {},
    ) =>
      request<Invite>(`/api/guilds/${guildId}/invites`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    previewInvite: (code: string) =>
      request<{
        code: string;
        guild: { id: string; name: string; iconUrl: string | null };
        expiresAt: string | null;
        uses: number;
        maxUses: number | null;
        memberCount: number;
      }>(`/api/invites/${code}`),
    acceptInvite: (code: string) =>
      request<{ guildId: string }>(`/api/invites/${code}/accept`, { method: 'POST' }),
    revokeInvite: (code: string) => request<void>(`/api/invites/${code}`, { method: 'DELETE' }),
  };
}
